/**
 * TodayPanel — Cluster H.7 (Feb 2026).
 *
 * "Today" rail rendered on the home screen below the law-chips. Three
 * sections, all reading from a single aggregator endpoint:
 *   • Compliance alerts (v1 placeholder — "No alerts" when empty)
 *   • Corpus updates    (count of new legal chunks in the past 7 days)
 *   • Team Herald previews (top 3 unread Herald notifications)
 *
 * Source: GET /api/v2/agent/dashboard/today.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  panel:      { en: 'Today',                 ar: 'اليوم' },
  alerts:     { en: 'Compliance alerts',     ar: 'تنبيهات الامتثال' },
  noAlerts:   { en: 'All current matters compliant', ar: 'جميع المسائل الحالية متوافقة' },
  corpus:     { en: 'Corpus updates',        ar: 'تحديثات المكتبة' },
  noCorpus:   { en: 'Corpus current',        ar: 'المكتبة محدّثة' },
  herald:     { en: 'Team Herald',           ar: 'تنبيهات الفريق' },
  noHerald:   { en: 'Inbox is clear',        ar: 'الصندوق فارغ' },
  newChunks:  { en: 'new sections in the last 7 days', ar: 'مقطعاً جديداً في آخر 7 أيام' },
};

export const TodayPanel = ({ isArabic }) => {
  const { session } = useAuth();
  const token = session?.access_token;
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) { setData({ alerts: [], corpus_updates: [], herald_previews: [] }); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/v2/agent/dashboard/today`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        });
        if (!cancelled) setData(res.data || { alerts: [], corpus_updates: [], herald_previews: [] });
      } catch (_e) {
        if (!cancelled) setData({ alerts: [], corpus_updates: [], herald_previews: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (!data) return null;

  const T = (k) => LABELS[k][isArabic ? 'ar' : 'en'];

  return (
    <div
      data-testid="today-panel"
      style={{ width: '100%', maxWidth: 680, margin: '24px 0 0' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div
        data-testid="today-panel-title"
        style={{
          // WCAG AA bump: #555 on white = 7.46:1 (AAA). Was #888.
          fontSize: 11,
          color: '#555555',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 10,
          textAlign: isArabic ? 'right' : 'left',
        }}
      >
        {T('panel')}
      </div>
      <div
        style={{
          display: 'grid',
          // I.A — Cluster I (Feb 2026): reflow Today panel into 1 column
          // at narrow widths (was hard `1fr 1fr 1fr` → 110px each at
          // 430px, content cramped against the section padding).
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 8,
        }}
      >
        <Section
          testId="today-section-alerts"
          title={T('alerts')}
          empty={T('noAlerts')}
          isArabic={isArabic}
          items={(data.alerts || []).map((a) => ({
            id: a.id,
            primary: (isArabic ? a.title_ar : a.title_en) || a.title || a.source,
            secondary: a.source,
          }))}
        />
        <Section
          testId="today-section-corpus"
          title={T('corpus')}
          empty={T('noCorpus')}
          isArabic={isArabic}
          items={(data.corpus_updates || []).map((c, i) => ({
            id: `${c.kind}-${i}`,
            primary: `${c.count} ${T('newChunks')}`,
            secondary: null,
          }))}
        />
        <Section
          testId="today-section-herald"
          title={T('herald')}
          empty={T('noHerald')}
          isArabic={isArabic}
          items={(data.herald_previews || []).map((h) => ({
            id: h.notification_id || h.message_id,
            primary: h.subject || h.preview,
            secondary: h.preview && h.subject ? h.preview : null,
            onClick: h.matter_id
              ? () => navigate(`/chat/${encodeURIComponent(h.matter_id)}`)
              : null,
          }))}
        />
      </div>
    </div>
  );
};

const Section = ({ title, empty, items, isArabic, testId }) => {
  return (
    <div
      data-testid={testId}
      style={{
        background: '#F5F5F3',
        border: '1px solid #E0E0E0',
        borderRadius: 6,
        padding: '10px 12px',
        minHeight: 96,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          // AA: #444 on #F5F5F3 ≈ 9.4:1
          fontSize: 11,
          color: '#444444',
          fontWeight: 500,
          marginBottom: 6,
          textAlign: isArabic ? 'right' : 'left',
        }}
      >
        {title}
      </div>
      {items.length === 0 ? (
        <div
          data-testid={`${testId}-empty`}
          style={{
            // AA bump: #5e5e5e ≈ 6.7:1 on #F5F5F3. Was #aaa = 2.7:1 FAIL.
            fontSize: 11, color: '#5e5e5e', textAlign: isArabic ? 'right' : 'left', flex: 1,
          }}
        >
          {empty}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.slice(0, 3).map((it) => (
            <li key={it.id}>
              <button
                type="button"
                onClick={it.onClick || (() => {})}
                disabled={!it.onClick}
                data-testid={`${testId}-item-${it.id}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: it.onClick ? 'pointer' : 'default',
                  textAlign: isArabic ? 'right' : 'left',
                  width: '100%',
                  color: '#222',
                  fontSize: 11,
                  lineHeight: 1.4,
                  fontFamily: 'inherit',
                }}
              >
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {it.primary}
                </div>
                {it.secondary && (
                  <div
                    style={{
                      // AA: #5e5e5e ≈ 6.7:1 on #F5F5F3
                      fontSize: 10,
                      color: '#5e5e5e',
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {it.secondary}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TodayPanel;
